import { AddCustomerNoteBody, type CustomerDetail } from "@cleanbudget/contract/customer";
import type { ViesResult } from "@cleanbudget/contract/customer-account";
import { Alert, AlertDescription } from "@cleanbudget/ui/components/alert";
import { Button } from "@cleanbudget/ui/components/button";
import { IconUserOff } from "@tabler/icons-react";
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { match } from "ts-pattern";
import { BackLink } from "@/components/back-link";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { DetailLayout, DetailList, DetailSection } from "@/components/detail-layout";
import { Form, FormError, SubmitButton, TextareaField, useZodForm } from "@/components/form/form";
import { PageHeader } from "@/components/page-header";
import { StatusBadge } from "@/components/status-badge";
import { apiErrorTag } from "@/lib/api-error";
import { formatDate, formatDateTime } from "@/lib/format";
import { orpc } from "@/lib/orpc";
import { AddressesSection } from "./-components/addresses-section";
import { BusinessDecision } from "./-components/business-decision";
import { AccountBadge, BusinessStatusBadge } from "./-components/customer-badges";
import { CustomerPricesSection } from "./-components/customer-prices";

const customerQuery = (customerId: string) => orpc.customer.get.queryOptions({ input: { customerId } });

/**
 * One customer (#77): their data, address book, business profile with the decision on it (#80),
 * customer prices (#81), orders (once there are any) and the staff's notes; and anonymising them
 * on request (#78), behind a confirmation.
 */
export const Route = createFileRoute("/_admin/customer/$customerId")({
	loader: ({ context, params }) => context.queryClient.ensureQueryData(customerQuery(params.customerId)),
	component: CustomerPage,
});

function CustomerPage() {
	const { t } = useTranslation("customer");
	const { customerId } = Route.useParams();
	const { data: customer } = useSuspenseQuery(customerQuery(customerId));
	const anonymised = customer.anonymisedAt !== null;

	return (
		<div className="flex flex-col gap-6">
			<PageHeader
				back={<BackLink to="/customer">{t("title")}</BackLink>}
				title={anonymised ? t("anonymisedTitle") : (customer.name ?? customer.email)}
				description={customer.name ? customer.email : undefined}
				help="customer:customer"
			/>
			{customer.anonymisedAt ? (
				<Alert>
					<IconUserOff aria-hidden />
					<AlertDescription>
						{t("detail.anonymisedAt", { date: formatDateTime(customer.anonymisedAt) })}
					</AlertDescription>
				</Alert>
			) : null}
			<DetailLayout
				aside={
					<>
						<NotesSection customer={customer} />
						{anonymised ? null : <AnonymiseSection customer={customer} />}
					</>
				}
			>
				<DataSection customer={customer} />
				<AddressesSection customer={customer} />
				<BusinessSection customer={customer} />
				<CustomerPricesSection customer={customer} />
				<DetailSection title={t("orders.title")} help="customer:orders">
					<p className="text-muted-foreground">{t("orders.empty")}</p>
				</DetailSection>
			</DetailLayout>
		</div>
	);
}

function DataSection({ customer }: { customer: CustomerDetail }) {
	const { t } = useTranslation("customer");
	const unknown = <span className="text-muted-foreground">{t("detail.unknown")}</span>;
	return (
		<DetailSection title={t("detail.data")} help="customer:data">
			<DetailList
				items={[
					{ label: t("detail.name"), value: customer.name ?? unknown },
					{
						label: t("detail.email"),
						help: "customer:email",
						value: customer.email,
					},
					{
						label: t("detail.phone"),
						value: customer.phone ? <a href={`tel:${customer.phone}`}>{customer.phone}</a> : unknown,
					},
					{
						label: t("detail.kind"),
						help: "customer:kind",
						value: t(`kind.${customer.kind}`),
					},
					{
						label: t("detail.account"),
						help: "customer:account",
						value: (
							<AccountBadge
								customer={{ account: customer.account.status, anonymisedAt: customer.anonymisedAt }}
							/>
						),
					},
					...(customer.account.createdAt
						? [
								{ label: t("detail.accountSince"), value: formatDate(customer.account.createdAt) },
								{
									label: t("detail.emailConfirmed"),
									value: customer.account.emailConfirmedAt
										? formatDate(customer.account.emailConfirmedAt)
										: t("detail.notConfirmed"),
								},
							]
						: []),
					{
						label: t("detail.newsletter"),
						value: customer.newsletter ? t("detail.newsletterYes") : t("detail.newsletterNo"),
					},
					{ label: t("detail.customerSince"), value: formatDate(customer.createdAt) },
				]}
			/>
		</DetailSection>
	);
}

function BusinessSection({ customer }: { customer: CustomerDetail }) {
	const { t } = useTranslation("customer");
	const profile = customer.businessProfile;
	if (profile === null) {
		return (
			<DetailSection title={t("business.title")} help="customer:businessProfile">
				<p className="text-muted-foreground">{t("business.none")}</p>
			</DetailSection>
		);
	}
	return (
		<DetailSection
			title={t("business.title")}
			help="customer:businessProfile"
			actions={<BusinessStatusBadge status={profile.status} />}
		>
			<div className="flex flex-col gap-4">
				<DetailList
					items={[
						{ label: t("business.companyName"), value: profile.companyName },
						{ label: t("business.kvkNumber"), value: profile.kvkNumber },
						{ label: t("business.vatId"), value: profile.vatId },
						{
							label: t("business.vies"),
							help: "customer:vies",
							value: (
								<span className="flex flex-col gap-1">
									<ViesBadge result={profile.vies.result} />
									<span className="text-xs text-muted-foreground">
										{t("business.viesCheckedAt", { date: formatDateTime(profile.vies.checkedAt) })}
									</span>
								</span>
							),
						},
						...(profile.vies.name ? [{ label: t("business.viesName"), value: profile.vies.name }] : []),
						...(profile.vies.address
							? [{ label: t("business.viesAddress"), value: profile.vies.address }]
							: []),
						{ label: t("business.appliedAt"), value: formatDateTime(profile.appliedAt) },
					]}
				/>
				<BusinessDecision customer={customer} />
			</div>
		</DetailSection>
	);
}

function ViesBadge({ result }: { result: ViesResult }) {
	const { t } = useTranslation("customer");
	return (
		<StatusBadge
			tone={match(result)
				.with("valid", () => "positive" as const)
				.with("invalid", () => "negative" as const)
				.with("unknown", () => "attention" as const)
				.exhaustive()}
		>
			{t(`business.viesResult.${result}`)}
		</StatusBadge>
	);
}

function NotesSection({ customer }: { customer: CustomerDetail }) {
	const { t } = useTranslation("customer");
	const { t: tCommon } = useTranslation();
	const queryClient = useQueryClient();
	const form = useZodForm(AddCustomerNoteBody, { defaultValues: { body: "" } });
	const add = useMutation({
		...orpc.customer.addNote.mutationOptions(),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: orpc.customer.key() }),
	});

	const onSubmit = async (values: AddCustomerNoteBody) => {
		try {
			await add.mutateAsync({ customerId: customer.id, ...values });
			form.reset({ body: "" });
		} catch (error) {
			form.setError("root", {
				type: "server",
				message: match(apiErrorTag(error))
					.with("CustomerAnonymised", () => t("notes.anonymised"))
					.otherwise(() => tCommon("form.failed")),
			});
		}
	};

	return (
		<DetailSection title={t("notes.title")} description={t("notes.description")} help="customer:notes">
			<div className="flex flex-col gap-5">
				{customer.anonymisedAt ? null : (
					<Form form={form} onSubmit={onSubmit} className="gap-3">
						<TextareaField control={form.control} name="body" label={t("notes.field")} rows={3} />
						<FormError control={form.control} />
						<div>
							<SubmitButton control={form.control} size="sm">
								{t("notes.add")}
							</SubmitButton>
						</div>
					</Form>
				)}
				{customer.notes.length === 0 ? (
					<p className="text-muted-foreground">{t("notes.empty")}</p>
				) : (
					<ol className="flex flex-col gap-4" aria-label={t("notes.title")}>
						{customer.notes.map((note) => (
							<li key={note.id} className="flex flex-col gap-1 border-l-2 border-border pl-3">
								<p className="break-words whitespace-pre-wrap">{note.body}</p>
								<p className="text-xs text-muted-foreground">
									<time dateTime={note.createdAt}>
										{t("notes.by", { name: note.employee.name, date: formatDateTime(note.createdAt) })}
									</time>
								</p>
							</li>
						))}
					</ol>
				)}
			</div>
		</DetailSection>
	);
}

function AnonymiseSection({ customer }: { customer: CustomerDetail }) {
	const { t } = useTranslation("customer");
	const queryClient = useQueryClient();
	const anonymise = useMutation({
		...orpc.customer.anonymise.mutationOptions(),
		onSuccess: (updated) => {
			queryClient.setQueryData(customerQuery(customer.id).queryKey, updated);
			return queryClient.invalidateQueries({ queryKey: orpc.customer.list.key() });
		},
	});

	return (
		<DetailSection
			title={t("anonymise.title")}
			description={t("anonymise.description")}
			help="customer:anonymise"
		>
			<ConfirmDialog
				trigger={
					<Button variant="destructive">
						<IconUserOff aria-hidden />
						{t("anonymise.action")}
					</Button>
				}
				title={t("anonymise.confirmTitle", { email: customer.email })}
				description={t("anonymise.confirmDescription")}
				confirmLabel={t("anonymise.confirm")}
				destructive
				onConfirm={() => anonymise.mutateAsync({ customerId: customer.id })}
				errorMessage={(error) =>
					match(apiErrorTag(error))
						.with("CustomerAnonymised", () => t("anonymise.already"))
						.otherwise(() => undefined)
				}
			/>
		</DetailSection>
	);
}
